import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Typography,
  Box,
  Paper
} from '@mui/material';
import { Deal, CreateDealPayload, DealNote } from '../types/deal';
import { useDealStagesStore } from '../store/dealStagesStore';
import { useOwnersStore } from '../store/ownersStore';
import { useFormOptionsStore } from '../store/formOptionsStore';
import { PIPELINE_ID, DEFAULT_OWNER_ID } from '../constants';
import { getDealNotes, getAssociatedPartners } from '../services/api';

interface DealFormProps {
  onSubmit: (data: CreateDealPayload) => void;
  onCancel: () => void;
  initialData: Deal | null;
  accessToken?: string;
}

export const DealForm: React.FC<DealFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  accessToken
}) => {
  const { dealStages } = useDealStagesStore();
  const { currentPartnerName, currentPartnerSalesRepEmail } = useOwnersStore();
  const { focusApps, loading, error } = useFormOptionsStore();

  const initialDealStage = initialData?.properties.dealstage || Object.keys(dealStages)[0] || '';
  const [dealNotes, setDealNotes] = React.useState<DealNote[]>([]);
  const [notesLoading, setNotesLoading] = React.useState(false);
  const [allowedPartners, setAllowedPartners] = React.useState<string[]>([]);
  const [partnersLoading, setPartnersLoading] = React.useState(false);

  // Show partner selection field only for new deals when current partner is Apty
  const showPartnerSelection = !initialData && currentPartnerName === 'Apty';

  // Fetch notes when editing a deal
  React.useEffect(() => {
    const fetchNotes = async () => {
      if (initialData?.id && accessToken) {
        try {
          setNotesLoading(true);
          const notesResponse = await getDealNotes(initialData.id, accessToken);
          console.log('Notes API response:', notesResponse);
          setDealNotes(notesResponse.results || []);
        } catch (error) {
          console.error('Failed to fetch deal notes:', error);
        } finally {
          setNotesLoading(false);
        }
      }
    };

    fetchNotes();
  }, [initialData?.id, accessToken]);

  // Fetch partners when needed (only for Apty users creating new deals)
  React.useEffect(() => {
    const fetchPartners = async () => {
      if (showPartnerSelection && accessToken && allowedPartners.length === 0) {
        try {
          setPartnersLoading(true);
          const partnersResponse = await getAssociatedPartners(accessToken);
          // Extract options from the HubSpot property response
          const options = partnersResponse.options?.map((option: any) => option.value) || [];
          setAllowedPartners(options);
        } catch (error) {
          console.error('Failed to fetch associated partners:', error);
          // Fallback to hardcoded list if API fails
          setAllowedPartners([]);
        } finally {
          setPartnersLoading(false);
        }
      }
    };

    fetchPartners();
  }, [showPartnerSelection, accessToken, allowedPartners.length]);

  const [formData, setFormData] = React.useState<CreateDealPayload>({
    properties: {
      hubspot_owner_id: initialData?.properties.hubspot_owner_id || DEFAULT_OWNER_ID,
      dealname: initialData?.properties.dealname || '',
      associated_partner: initialData?.properties.associated_partner || currentPartnerName || '',
      amount: initialData?.properties.amount || '',
      number_of_applications__c: initialData?.properties.number_of_applications__c || '',
      focus_application: initialData?.properties.focus_application || '',
      focus_application_name__other_: initialData?.properties.focus_application_name__other_ || '',
      partner_sales_rep_name: initialData?.properties.partner_sales_rep_name || currentPartnerSalesRepEmail ||'',
      number_of_users__c: initialData?.properties.number_of_users__c || '',
      dealstage: initialDealStage,
      dealstageName: dealStages[initialDealStage] || '',
      pipeline: initialData?.properties.pipeline || PIPELINE_ID,
      notes: ''
    }
  });

  const handleChange = (field: keyof CreateDealPayload['properties']) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (field === 'dealstage') {
      setFormData({
        properties: {
          ...formData.properties,
          dealstageName: dealStages[event.target.value],
          [field]: event.target.value
        }
      });
      return;
    }
    setFormData({
      properties: {
        ...formData.properties,
        [field]: event.target.value
      }
    });
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (loading) {
    return (
      <Dialog open={true} onClose={onCancel}>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={true} onClose={onCancel}>
        <DialogContent>
          Error loading form options: {error}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onCancel} maxWidth="md" fullWidth className='modal-content'>
      <form onSubmit={handleSubmit}>
        <DialogTitle 
          className='modal-header title' 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 10,
            borderBottom: '1px solid #e0e0e0',
            paddingBottom: 2
          }}
        >
          <span>{initialData ? `${'Edit Deal (Last Updated: ' + formatDate(initialData.updatedAt) + ')'}` : 'Create New Deal'}</span>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button className='btn-cancel' onClick={onCancel} variant="outlined">
              Cancel
            </Button>
            <Button className='btn-save' type="submit" variant="contained" color="primary">
              {initialData ? 'Update' : 'Create'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Deal Name"
                value={formData.properties.dealname}
                onChange={handleChange('dealname')}
                required
              />
            </Grid>
            {showPartnerSelection && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Associated Partner"
                  value={formData.properties.associated_partner}
                  onChange={handleChange('associated_partner')}
                  required
                  disabled={partnersLoading}
                  helperText={partnersLoading ? "Loading partners..." : ""}
                >
                  {allowedPartners.map((partner) => (
                    <MenuItem key={partner} value={partner}>
                      {partner}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Deal Stage"
                value={formData.properties.dealstage}
                onChange={handleChange('dealstage')}
                required
              >
                {Object.entries(dealStages).map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Applications"
                type="number"
                value={formData.properties.number_of_applications__c}
                onChange={handleChange('number_of_applications__c')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Focus Application"
                value={formData.properties.focus_application}
                onChange={handleChange('focus_application')}
              >
                {focusApps.map((app) => (
                  <MenuItem key={app.value} value={app.value}>
                    {app.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {
              formData.properties.focus_application === 'Other' &&
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Other Focus Application"
                  value={formData.properties.focus_application_name__other_}
                  onChange={handleChange('focus_application_name__other_')}
                />
              </Grid>
            }
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Users"
                type="number"
                value={formData.properties.number_of_users__c}
                onChange={handleChange('number_of_users__c')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.properties.amount}
                onChange={handleChange('amount')}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sales Executive"
                value={formData.properties.partner_sales_rep_name}
                onChange={handleChange('partner_sales_rep_name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={4}
                value={formData.properties.notes || ''}
                onChange={handleChange('notes')}
                placeholder="Add any additional comments about this deal..."
                required
              />
            </Grid>
            {initialData && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Previous Comments
                </Typography>
                {notesLoading ? (
                  <CircularProgress size={24} />
                ) : dealNotes.length > 0 ? (
                  <Box>
                    {dealNotes.map((note, index) => {
                      // Handle different possible note structures
                      const noteData = note.properties || note;
                      const timestamp = noteData?.hs_timestamp || noteData?.hs_createdate || noteData?.createdAt || noteData?.updatedAt;
                      const rawContent = noteData?.hs_note_body || noteData?.body || noteData?.content;
                      
                      // Strip HTML tags from content
                      const stripHtml = (html: string) => {
                        const tmp = document.createElement('div');
                        tmp.innerHTML = html;
                        return tmp.textContent || tmp.innerText || '';
                      };
                      
                      const content = rawContent ? stripHtml(rawContent) : 'No content available';
                      
                      return (
                        <Paper key={note.id || index} sx={{ p: 2, mb: 1, border: '1px solid #e0e0e0' }}>
                          <Typography variant="body2" color="text.secondary">
                            {timestamp ? 
                              new Date(timestamp).toLocaleString() : 
                              'No timestamp available'
                            }
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                            {content}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No previous comments found.
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </form>
    </Dialog>
  );
}; 
